import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, input, signal, Signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {distinctUntilChanged, filter, Observable, scan, switchMap} from "rxjs";
import {map, shareReplay} from "rxjs/operators";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {AgendaelementId, MøteId, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {Leveringsvalg} from "felles-ng2/tjenester/datalager/datatjeneste";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {tomAgendaelementModell} from "felles-ng2/modell/tommeModeller";
import {Sakstidspunkt, Sakstidspunkter, SakstidspunkterBeregning} from "felles-ng2/tjenester/Sakstidspunkter";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MatDialog} from "@angular/material/dialog";
import {KopierSakDialogKomponent} from "../../modale/kopier-sak-dialog/kopier-sak-dialog";
import {FlyttSakDialogKomponent} from "../../modale/flytt-sak-dialog/flytt-sak-dialog";
import {PauseRedigerKomponent} from "../pause-rediger/pause-rediger";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIconButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {TittelKomponent, TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {ToggleKlasseVedStickyDirektiv} from "web-ng2/delt/direktiver/toggleKlasseVedSticky/toggleKlasseVedSticky";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";

const debug = initDebugger('PauseVisKomponent');

/**
 * Viser pause med tilhørende detaljer som starttidspunkt, varighet og beskrivelse.
 * Pause-varianten av {@link SakVisKomponent} - en pause har ikke referat, dokumenter, oppgaver,
 * avstemninger, beslutninger eller kommentarer.
 */
@Component({
    selector: 'pause-vis',
    templateUrl: 'pause-vis.html',
    styleUrl: 'pause-vis.scss',
    host: {
        'data-testid': 'saksdetaljer',
        'class': 'er-pause'
    },
    imports: [
        PauseRedigerKomponent,
        MatIcon,
        MatTooltip,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        TranslateModule,
        HtmlFormatertKomponent,
        ZoneddatetimeSomPlainTimePipe,
        FormaterPlainTimePipe,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        FormaterDurationPipe,
        TittelVerdiSamlingKomponent,
        TittelVerdiKomponent,
        TittelKomponent,
        VerdiKomponent,
        ToggleKlasseVedStickyDirektiv,
        BekreftKlikkDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PauseVisKomponent {

    id = input.required<AgendaelementId>();

    pause: Signal<AgendaelementModellDLM>;
    arbeider = asynkArbeidPågårSignal();
    redigeringMulig = computed(() =>
      this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.pause().eiendeGruppeId) &&
      !this.pause().moete.laastForRedigering);
    kopierMulig = computed(() => this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.pause().eiendeGruppeId));
    menyDeaktivert = this.navigasjonDeaktivering.navigasjonDeaktivertSomSignal;

    sakstidspunkt: Signal<Sakstidspunkt>;
    erRedigerPause = signal(false);

    /** Sakstidspunkt-beregningen for møtet pausen tilhører - kreves av {@link PauseRedigerKomponent}
     * for å kunne propagere varighetsendringer til møtesiden mens pausen redigeres */
    sakstidspunkterBeregning = signal<SakstidspunkterBeregning | null>(null);

    constructor(private avhengigheter: PauseVisKomponentAvhengigheter,
                private sakstidspunkterTjeneste: Sakstidspunkter,
                private navigasjon: Navigasjon,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private kontekst: BrukerkontekstTilbyder,
                private dialog: MatDialog,
                private destroyRef: DestroyRef) {
        //siden det opprettes multiple abonnementer til pauseSomObserverbar sørger vi her for at ikke hvert abonnement fører til et nytt kall til lastPause(..)
        const pauseSomObserverbar = this.lastPause();

        this.pause = toSignal(pauseSomObserverbar, {initialValue: tomAgendaelementModell()});
        this.sakstidspunkt = toSignal(
          pauseSomObserverbar.pipe(
            switchMap(pause => this.sakstidspunkterTjeneste.forMøteSomObserverbar(pause.moete.id)),
            //dersom pause har blitt slettet eller flyttet KAN det være at vi får levert tidspunkter som ikke inneholder pausen fordi takeUntilDestroyed ikke har hatt effekt enda
            filter(tidspunkter => tidspunkter.harForSak(this.pause().id)),
            map(tidspunkter => tidspunkter.forSak(this.pause().id))
          ),
          {initialValue: Sakstidspunkt.utenTidspunkt('')}
        );

        pauseSomObserverbar.pipe(map(pause => pause.moete.id), distinctUntilChanged(), takeUntilDestroyed())
          .subscribe(møteId => this.settSakstidspunkterBeregning(this.sakstidspunkterTjeneste.forMøte(møteId)));
        this.destroyRef.onDestroy(() => this.settSakstidspunkterBeregning(null));
    }

    redigerPause() {
        this.erRedigerPause.set(true);
    }

    redigerPauseFerdig() {
        this.erRedigerPause.set(false);
    }

    flyttPause() {
        FlyttSakDialogKomponent.vis(this.dialog, Promise.resolve(this.pause()));
    }

    kopierPause() {
        KopierSakDialogKomponent.vis(this.dialog, Promise.resolve(this.pause()));
    }

    async slettPause() {
        const pause = this.pause();
        //dette kallet fører til at lastPause(..) leverer 'undefined' - dette detekterer vi i konstruktør og går da til møtesiden
        await this.avhengigheter.slettPause(pause.id);
        await this.avhengigheter.relastMøte(pause.moete.id);
    }

    // hjelpemetoder

    /** Laster pause når id endres, sørger for at pausen kun lastes én gang selv om det opprettes multiple subscriptions.
     * Håndterer også navigasjon til møtesiden dersom pausen slettes. */
    private lastPause(): Observable<AgendaelementModellDLM> {
        return toObservable(this.id).pipe(
          switchMap(id => this.arbeider.startArbeidAvsluttVedLeveranse(this.avhengigheter.lastPause(id))),
          //når pausen slettes returnerer lastPause(..) undefined, da må vi relaste møte (pga mulig endring i saksnummer) og gå til møtesiden
          scan((forrigePause, nåværendePause) => {
              if (nåværendePause === undefined && erIkkeNil(forrigePause)) {
                  this.avhengigheter.relastMøte(forrigePause.moete.id);
                  this.navigasjon.gåTilMøte(forrigePause.moete.id);
              }
              return nåværendePause;
          }, tomAgendaelementModell() as AgendaelementModellDLM | undefined),
          //ikke lever videre dersom pausen har blitt slettet
          filter(pause => pause !== undefined),
          //sørger for at ikke hver subscription fører til kall til avhengigheter.lastPause,
          //`refCount: true` slik at kilde unsubscribes når alle subscriptions har terminert
          shareReplay({bufferSize: 1, refCount: true})
        );
    }

    /** Setter ny sakstidspunkt-beregning og terminerer eventuell forrige (slik at tjenesten kan rydde opp) */
    private settSakstidspunkterBeregning(ny: SakstidspunkterBeregning | null) {
        this.sakstidspunkterBeregning()?.terminer();
        this.sakstidspunkterBeregning.set(ny);
    }
}

@Injectable()
export class PauseVisKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste,
                private møteDatatjeneste: MøteDatatjeneste) {
    }

    lastPause(id: AgendaelementId): Observable<AgendaelementModellDLM | undefined> {
        return this.sakDatatjeneste.hent(id, Leveringsvalg.lever_undefined_ved_slettet);
    }

    async slettPause(id: AgendaelementId): Promise<void> {
        await this.sakDatatjeneste.slett(id);
    }

    async relastMøte(møteId: MøteId): Promise<void> {
        await this.møteDatatjeneste.relast(møteId);
    }

}
